<template>
  <div v-if="terms.length">
    <vue-final-modal
      v-model="showModal"
      classes="modal-container"
      content-class="modal-content w-600"
    >
      <div class="modal-title">
        <div class="modal-title-left">
          <img src="/images/close-icon.png" :alt="$t('vocabulary.close')" @click="toggleModal"/>
        </div>
        <h3>{{$t('vocabulary.title')}}</h3>
        <div class="modal-title-right"/>
      </div>
      <div class="modal-body vocabulary">
        <p class="vocabulary__hint">{{$t('vocabulary.hint')}}</p>
        <div class="vocabulary__list">
          <div
            v-for="(item, index) in terms"
            :key="`${item.term['zh-Hant']}-${index}`"
            class="vocabulary__item"
            :class="{ 'vocabulary__item--open': selectedIndex === index }"
          >
            <button
              type="button"
              class="vocabulary__heading"
              :aria-expanded="selectedIndex === index"
              @click="toggleTerm(index)"
            >
              <span>
                <strong>{{localizedValue(item.term)}}</strong>
                <small v-if="secondaryValue(item.term)">{{secondaryValue(item.term)}}</small>
              </span>
              <span class="vocabulary__indicator">{{selectedIndex === index ? '−' : '+'}}</span>
            </button>
            <div v-if="selectedIndex === index" class="vocabulary__content">
              <div class="vocabulary__definition">
                <p>
                  <strong>{{$t('vocabulary.chinese_definition')}}</strong>
                  {{item.definition['zh-Hant']}}
                </p>
                <p>
                  <strong>{{$t('vocabulary.english_definition')}}</strong>
                  {{item.definition.en}}
                </p>
              </div>
              <div class="vocabulary__speech">
                <button type="button" @click="speak(speechText(item, 'en'), 'en-US')">
                  🔊 {{$t('vocabulary.english')}}
                </button>
                <button type="button" @click="speak(speechText(item, 'zh-Hant'), 'zh-HK')">
                  🔊 {{$t('vocabulary.cantonese')}}
                </button>
                <button type="button" @click="speak(speechText(item, 'zh-Hant'), 'zh-CN')">
                  🔊 {{$t('vocabulary.mandarin')}}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </vue-final-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { createSpeechPlayer } from '@/utils/speech'

export default {
  name: 'VocabularyModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    terms: {
      type: Array,
      default: () => []
    }
  },
  emits: ['toggle'],
  data () {
    return {
      showModal: false,
      selectedIndex: null,
      speechPlayer: createSpeechPlayer()
    }
  },
  computed: {
    ...mapGetters({
      language: 'auth/language'
    })
  },
  methods: {
    toggleModal () {
      this.showModal = false
    },
    toggleTerm (index) {
      this.speechPlayer.stop()
      this.selectedIndex = this.selectedIndex === index ? null : index
    },
    localizedValue (value) {
      return value[this.language] || value['zh-Hant'] || value.en || ''
    },
    secondaryValue (value) {
      const secondaryLanguage = this.language === 'en' ? 'zh-Hant' : 'en'
      const secondary = value[secondaryLanguage]
      return secondary && secondary !== this.localizedValue(value) ? secondary : ''
    },
    speechText (item, language) {
      // Never read the visible bilingual row or another language as a fallback.
      return [item.term?.[language], item.definition?.[language]]
        .filter(value => typeof value === 'string' && value.trim())
        .map(value => value.trim())
        .join(language === 'en' ? '. ' : '。')
    },
    speak (text, language) {
      const errorKey = this.speechPlayer.speak(text, language, {
        onError: () => window.alert(this.$t('vocabulary.speech_failed'))
      })
      if (errorKey) window.alert(this.$t(errorKey))
    }
  },
  beforeUnmount () {
    this.speechPlayer.stop()
  },
  watch: {
    show (value) {
      this.showModal = value
      if (!value) {
        this.selectedIndex = null
        this.speechPlayer.stop()
      }
    },
    showModal (value) {
      if (!value) this.speechPlayer.stop()
      this.$emit('toggle', value)
    },
    terms () {
      this.speechPlayer.stop()
      this.selectedIndex = null
    },
    language () {
      this.speechPlayer.stop()
    }
  }
}
</script>

<style lang="scss">
@import "style";
</style>
